package com.exura.features.plandefinition.data.mappers.plandefinition

import com.exura.features.firebase.data.mappers.SerializableMappers
import com.exura.features.plandefinition.data.remote.dto.element.BasePlanElementDto
import com.exura.features.plandefinition.data.remote.dto.element.FloatingElementDto
import com.exura.features.plandefinition.data.remote.dto.element.InstructionElementDto
import com.exura.features.plandefinition.data.remote.dto.element.PersistentElementDto
import com.exura.features.plandefinition.data.remote.dto.element.QuestionElementDto
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.element.FloatingElement
import com.exura.features.plandefinition.domain.model.element.InstructionElement
import com.exura.features.plandefinition.domain.model.element.PersistentElement
import com.exura.features.plandefinition.domain.model.element.QuestionElement
import dev.gitlive.firebase.firestore.CollectionReference

/**
 * Base interface for all plan element mappers.
 * Extends SerializableMappers for Firebase storage compatibility.
 *
 * @param T The specific BasePlanElement implementation type
 * @param D The specific BasePlanElementDto implementation type
 */
interface PlanElementMappers<T : BasePlanElement, D : BasePlanElementDto> : SerializableMappers<T, D>

/**
 * Generic function for converting a BasePlanElement domain model to its corresponding DTO.
 * This should be used when the specific element type is unknown at compile time.
 *
 * @param model The domain model to convert
 * @return The corresponding DTO
 */
fun BasePlanElement.toDto(): BasePlanElementDto {
    return when (this) {
        is InstructionElement -> InstructionElementMappers().toDto(this)
        is PersistentElement -> PersistentElementMappers().toDto(this)
        is QuestionElement -> QuestionElementMappers().toDto(this)
        is FloatingElement -> FloatingElementMappers().toDto(this)
        else -> throw IllegalArgumentException("Unknown element type: ${this::class.simpleName}")
    }
}

/**
 * Generic function for converting a BasePlanElementDto to its corresponding domain model.
 * This should be used when the specific DTO type is unknown at compile time.
 *
 * @param collection The Firestore collection reference
 * @param id The document ID
 * @return The corresponding domain model
 */
fun BasePlanElementDto.toDomain(collection: CollectionReference? = null, id: String? = null): BasePlanElement {
    return when (this) {
        is InstructionElementDto -> InstructionElementMappers().toDomain(this, collection, id)
        is PersistentElementDto -> PersistentElementMappers().toDomain(this, collection, id)
        is QuestionElementDto -> QuestionElementMappers().toDomain(this, collection, id)
        is FloatingElementDto -> FloatingElementMappers().toDomain(this, collection, id)
        else -> throw IllegalArgumentException("Unknown element DTO type: ${this::class.simpleName}")
    }
}
